/**
 * `PortEventsMap` — MapLibre GL map layer for port-rejection / transit events.
 *
 * Renders a dark-style base map with:
 *   - Status-coloured DOM markers per event type (origin/accepted/refused/
 *     disputed/transit/disembark).
 *   - Dashed route polyline connecting events in chronological order.
 *   - Live SSE updates via `usePortEventsStream` — markers and polyline
 *     rebuild in place without a React re-render of the canvas.
 *   - Click/Enter popup: portName, eventType badge, date, notes, source link.
 *   - Reconnecting badge with spinning loader icon and "Retry now" button.
 *   - Toggleable legend panel anchored to the bottom-end corner.
 *
 * SSR safety: MapLibre touches `window` and `HTMLCanvasElement` at
 * construction time — the instance is created inside `useEffect` only.
 * A skeleton placeholder is shown until the tile style finishes loading.
 *
 * RTL: inline-start/end positioning throughout; source-link icon carries
 * inline `style={{ transform: "scaleX(var(--rtl-flip, 1))" }}` since
 * the CSS class `rtl:scale-x-[-1]` is unavailable inside the MapLibre
 * popup DOM subtree (outside the React tree).
 *
 * Touch targets: the legend toggle button has `minHeight: 44px` via the
 * parent container — NOT applied directly to a Shadcn Switch.
 */

import type { FeatureCollection, LineString } from "geojson";
import maplibregl, {
  type GeoJSONSource,
  type LngLatLike,
  type Map as MapLibreMap,
  type Marker as MapLibreMarker,
  type Popup as MapLibrePopup,
} from "maplibre-gl";
import { useEffect, useRef, useState } from "react";

import type { PortEventFrame } from "#/hooks/usePortEventsStream.js";
import { usePortEventsStream } from "#/hooks/usePortEventsStream.js";
import type { TranslateFn } from "#/lib/i18n.js";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAP_STYLE_URL = "https://tiles.openfreemap.org/styles/dark";
const DEFAULT_CENTER: LngLatLike = [0, 20];
const DEFAULT_ZOOM = 2;

const EVENT_COLORS: Record<string, string> = {
  origin: "#9ca3af",
  accepted: "#22c55e",
  refused: "#ef4444",
  disputed: "#f59e0b",
  transit: "#3b82f6",
  disembark: "#a855f7",
};

function colorFor(eventType: string): string {
  return EVENT_COLORS[eventType] ?? "#9ca3af";
}

// ---------------------------------------------------------------------------
// GeoJSON helpers
// ---------------------------------------------------------------------------

function eventsToRouteGeojson(events: PortEventFrame[]): FeatureCollection<LineString> {
  if (events.length < 2) return { type: "FeatureCollection", features: [] };
  const sorted = [...events].sort(
    (a, b) => new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime(),
  );
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: sorted.map((e) => [e.lng, e.lat]),
        },
        properties: {},
      },
    ],
  };
}

// ---------------------------------------------------------------------------
// DOM helpers
// ---------------------------------------------------------------------------

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function buildPopupHtml(event: PortEventFrame, t: TranslateFn): string {
  const color = colorFor(event.eventType);
  const parts: string[] = [
    `<strong style="font-family:var(--font-sans);font-size:0.9rem;display:block;">${escapeHtml(event.portName)}${event.countryCode ? ` <span style="font-size:0.75rem;font-weight:400;opacity:0.7;">${escapeHtml(event.countryCode)}</span>` : ""}</strong>`,
    `<span style="display:inline-block;margin-top:0.375rem;padding:0.125rem 0.5rem;border-radius:0.25rem;font-size:0.72rem;font-weight:600;background:${color}22;color:${color};border:1px solid ${color}55;">${escapeHtml(t(`portEvents.eventType.${event.eventType}`))}</span>`,
    `<p style="margin:0.375rem 0 0;font-size:0.78rem;opacity:0.75;">${fmtDate(event.occurredAt)}</p>`,
  ];
  if (event.notes) {
    parts.push(`<p style="margin:0.375rem 0 0;font-size:0.8rem;">${escapeHtml(event.notes)}</p>`);
  }
  // External-link icon inside the popup cannot use Tailwind rtl: variant since
  // it lives outside the React tree. We mirror it via an inline CSS var trick.
  parts.push(
    `<p style="margin:0.5rem 0 0;"><a href="${escapeHtml(event.sourceUrl)}" target="_blank" rel="noopener noreferrer" style="color:var(--color-accent-info,#60a5fa);text-decoration:underline;display:inline-flex;align-items:center;gap:0.25rem;font-size:0.8rem;">${escapeHtml(t("portEvents.source"))}<svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg></a></p>`,
  );
  return `<div style="font-family:var(--font-sans,system-ui);max-width:260px;">${parts.join("")}</div>`;
}

function buildMarkerElement(eventType: string): HTMLDivElement {
  const color = colorFor(eventType);
  const el = document.createElement("div");
  el.style.cssText = `width:18px;height:18px;border-radius:50%;background:${color};border:2px solid rgba(255,255,255,0.75);cursor:pointer;box-shadow:0 1px 4px rgba(0,0,0,0.5);`;
  el.setAttribute("role", "button");
  el.tabIndex = 0;
  return el;
}

// ---------------------------------------------------------------------------
// Props + component
// ---------------------------------------------------------------------------

export type PortEventsMapProps = {
  /** MMSI of the vessel whose port events to display. Renders nothing when falsy. */
  vesselMmsi?: string | null;
  /** Server-fetched initial events (SSR hydration). */
  initialEvents?: PortEventFrame[];
  /** Locale-resolved translation function. */
  t: TranslateFn;
  /** Override API base URL — used by tests. */
  apiBaseUrl?: string;
};

export function PortEventsMap({
  vesselMmsi,
  initialEvents = [],
  t,
  apiBaseUrl,
}: PortEventsMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const markersRef = useRef<MapLibreMarker[]>([]);
  const popupRef = useRef<MapLibrePopup | null>(null);
  const hasFlewRef = useRef(false);

  const [isStyleLoaded, setIsStyleLoaded] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const [legendOpen, setLegendOpen] = useState(false);

  // Mirror latest props into refs so effects see fresh values without
  // re-running mount-only setup.
  const tRef = useRef(t);
  tRef.current = t;

  const { events, reconnecting, retry } = usePortEventsStream(vesselMmsi, initialEvents, {
    enabled: Boolean(vesselMmsi),
    apiBaseUrl,
  });

  // Keep a stable ref to the latest events for the popup handler closures.
  const eventsRef = useRef(events);
  eventsRef.current = events;

  // --------------------------------------------------------------------------
  // 1. Map construction (mount only).
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    let map: MapLibreMap;
    try {
      map = new maplibregl.Map({
        container,
        style: MAP_STYLE_URL,
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        attributionControl: { compact: true },
        pitch: 0,
        bearing: 0,
        pitchWithRotate: false,
        dragRotate: false,
        renderWorldCopies: false,
      });
    } catch {
      setHasFailed(true);
      return undefined;
    }

    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new maplibregl.ScaleControl({ unit: "metric" }), "bottom-right");

    map.on("error", (e) => {
      console.warn("[PortEventsMap]", (e as { error?: { message?: string } })?.error?.message ?? e);
    });

    map.on("load", () => {
      map.addSource("port-route", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "port-route-line",
        type: "line",
        source: "port-route",
        layout: { "line-join": "round", "line-cap": "round" },
        paint: {
          "line-color": "hsl(215 70% 65%)",
          "line-width": 2,
          "line-opacity": 0.55,
          "line-dasharray": [4, 3],
        },
      });
      setIsStyleLoaded(true);
    });

    return () => {
      popupRef.current?.remove();
      popupRef.current = null;
      for (const m of markersRef.current) m.remove();
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // --------------------------------------------------------------------------
  // 2. Sync events → markers + polyline whenever events or style changes.
  // --------------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isStyleLoaded) return;

    // Rebuild markers from scratch (events array grows by deduplication).
    for (const m of markersRef.current) m.remove();
    markersRef.current = [];
    popupRef.current?.remove();
    popupRef.current = null;

    for (const event of events) {
      const el = buildMarkerElement(event.eventType);
      const lngLat: LngLatLike = [event.lng, event.lat];

      const openPopup = () => {
        popupRef.current?.remove();
        popupRef.current = new maplibregl.Popup({
          closeButton: true,
          closeOnClick: true,
          maxWidth: "300px",
        })
          .setLngLat(lngLat)
          .setHTML(buildPopupHtml(event, tRef.current))
          .addTo(map);
      };

      el.addEventListener("click", openPopup);
      el.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openPopup();
        }
      });

      el.setAttribute("aria-label", `${event.portName} — ${event.eventType}`);

      const marker = new maplibregl.Marker({ element: el }).setLngLat(lngLat).addTo(map);
      markersRef.current.push(marker);
    }

    // Update dashed route polyline.
    const source = map.getSource("port-route") as GeoJSONSource | undefined;
    source?.setData(eventsToRouteGeojson(events));

    // Fly to first event once.
    if (events.length > 0 && !hasFlewRef.current) {
      hasFlewRef.current = true;
      const first = events[0];
      if (first) {
        map.flyTo({ center: [first.lng, first.lat], zoom: 3, essential: true });
      }
    }
  }, [events, isStyleLoaded]);

  // --------------------------------------------------------------------------
  // 3. Render
  // --------------------------------------------------------------------------

  if (!vesselMmsi) return null;

  if (hasFailed) {
    return (
      <section className="port-events-map-container" aria-label={t("portEvents.mapAriaLabel")}>
        <div className="port-events-map-fallback">
          <strong style={{ display: "block", marginBottom: "0.5rem" }}>
            {t("portEvents.mapUnavailableTitle")}
          </strong>
          <p style={{ margin: 0, maxWidth: "40ch" }}>{t("portEvents.mapUnavailableBody")}</p>
        </div>
      </section>
    );
  }

  return (
    <section
      className="port-events-map-container"
      aria-label={t("portEvents.mapAriaLabel")}
      style={{ position: "relative" }}
    >
      {/* MapLibre canvas mount point — explicit height required */}
      <div
        ref={containerRef}
        className="outbreak-map-canvas"
        data-testid="port-events-map-canvas"
        style={{ width: "100%", height: "420px", borderRadius: "0.5rem", overflow: "hidden" }}
      />

      {!isStyleLoaded && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "hsl(220 14% 14%)",
            borderRadius: "0.5rem",
            color: "hsl(215 20% 65%)",
            fontSize: "0.875rem",
          }}
        >
          {t("portEvents.mapLoading")}
        </div>
      )}

      {/* Reconnecting badge */}
      {reconnecting && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "absolute",
            top: "0.75rem",
            left: "50%",
            transform: "translateX(-50%)",
            background: "hsl(220 14% 12% / 0.92)",
            color: "hsl(0 0% 100%)",
            borderRadius: "0.375rem",
            border: "1px solid hsl(220 14% 32%)",
            padding: "0.375rem 0.875rem",
            fontSize: "0.78rem",
            display: "flex",
            alignItems: "center",
            gap: "0.375rem",
            zIndex: 20,
          }}
        >
          {/* Loader2-style spinner */}
          <svg
            aria-hidden="true"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            style={{ animation: "spin 1s linear infinite" }}
          >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          {t("portEvents.reconnecting")}
          <button
            type="button"
            onClick={retry}
            style={{
              background: "none",
              border: "none",
              color: "inherit",
              textDecoration: "underline",
              cursor: "pointer",
              padding: 0,
              fontSize: "inherit",
              marginInlineStart: "0.25rem",
            }}
          >
            {t("portEvents.retryNow")}
          </button>
        </div>
      )}

      {/* Legend toggle — WCAG touch target via min-height on this button */}
      <button
        type="button"
        onClick={() => setLegendOpen((v) => !v)}
        aria-expanded={legendOpen}
        aria-controls="port-events-legend"
        aria-label={t("portEvents.legendToggle")}
        style={{
          position: "absolute",
          bottom: "1.25rem",
          insetInlineEnd: "0.75rem",
          background: "hsl(220 14% 12% / 0.92)",
          color: "hsl(0 0% 100%)",
          border: "1px solid hsl(220 14% 32%)",
          borderRadius: "0.375rem",
          padding: "0 0.75rem",
          minHeight: "44px",
          fontSize: "0.78rem",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "0.375rem",
          zIndex: 20,
        }}
      >
        {/* Grid / legend icon */}
        <svg
          aria-hidden="true"
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
        </svg>
        {t("portEvents.legend")}
      </button>

      {/* Legend panel */}
      {legendOpen && (
        <div
          id="port-events-legend"
          role="complementary"
          aria-label={t("portEvents.legend")}
          style={{
            position: "absolute",
            bottom: "4.5rem",
            insetInlineEnd: "0.75rem",
            background: "hsl(220 14% 12% / 0.96)",
            color: "hsl(0 0% 93%)",
            border: "1px solid hsl(220 14% 32%)",
            borderRadius: "0.5rem",
            padding: "0.625rem 0.875rem",
            fontSize: "0.78rem",
            zIndex: 20,
            minWidth: "148px",
          }}
        >
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: "0.375rem",
            }}
          >
            {Object.entries(EVENT_COLORS).map(([type, color]) => (
              <li key={type} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span
                  aria-hidden="true"
                  style={{
                    width: "11px",
                    height: "11px",
                    borderRadius: "50%",
                    background: color,
                    flexShrink: 0,
                    border: "1.5px solid rgba(255,255,255,0.55)",
                  }}
                />
                {t(`portEvents.eventType.${type}`)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Screen-reader description */}
      <p
        style={{
          position: "absolute",
          inlineSize: "1px",
          blockSize: "1px",
          margin: "-1px",
          padding: 0,
          overflow: "hidden",
          clip: "rect(0,0,0,0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {t("portEvents.srDescription", { count: events.length })}
      </p>
    </section>
  );
}
